import React from "react";
import {
  Paper,
  Text,
  SimpleGrid,
  Group,
  Button,
  Anchor,
  Badge,
} from "@mantine/core";
import StyledPageTitle from "../components/StyledPageTitle";
import { useGetJobQuery, useUpdateJobMutation } from "../graphql";
import { useNavigate, useParams } from "react-router-dom";
import DateDisplay from "../components/DateDisplay";
import { IconCurrencyDollar, IconEdit } from "@tabler/icons-react";
import FinishJobsButton from "../components/FinishJobsButton";
import MarkJobsAsTodoButton from "../components/MarkJobsAsTodoButton";
import DeleteButton from "../components/DeleteButton";
import { usePermissions } from "../contexts/PermissionsContext";
import { track } from "../utils/analytics";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../utils/showNotification";
import { RACKET_TYPE_LABELS } from "../utils/racketType";

const JobsDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const params = useParams<{ id: string }>();
  const jobId = `${params.id}`;

  const getJob = useGetJobQuery({
    variables: {
      id: jobId,
    },
  });

  // UpdateJob returns the same JobWithRelations fragment GetJob reads, so the
  // normalized cache updates the page without a refetch.
  const [updateJob, updateJobResponse] = useUpdateJobMutation({
    onError: (error) => showErrorNotification(error.message),
  });

  const job = getJob.data?.getJob;

  const togglePaid = async () => {
    if (!job) return;
    const markingPaid = !job.paidAt;
    track("job_paid_toggled", { paid: markingPaid });
    // Only send the field being changed so a stale cached status is never
    // written back over a change made elsewhere.
    const result = await updateJob({
      variables: {
        input: {
          id: job.id,
          paidAt: markingPaid ? new Date().toISOString() : null,
        },
      },
    });
    if (result.data?.updateJob) {
      showSuccessNotification(
        markingPaid ? "Job marked as paid" : "Job marked as unpaid"
      );
    }
  };

  return (
    <>
      <Group justify="space-between" mb="sm">
        <StyledPageTitle>Job Details</StyledPageTitle>
        <Group gap="sm">
          <MarkJobsAsTodoButton
            hidden={job?.status === "TODO"}
            jobIds={[jobId]}
            onComplete={() => getJob.refetch()}
          />
          <FinishJobsButton
            jobIds={[jobId]}
            hidden={job?.status === "FINISHED"}
            onComplete={() => getJob.refetch()}
          />
          <Button
            id="btn-toggle-job-paid"
            data-testid="toggle-job-paid-button"
            rightSection={<IconCurrencyDollar />}
            onClick={togglePaid}
            disabled={!job}
            loading={updateJobResponse.loading}
            color={job?.paidAt ? "orange" : "green"}
            display={!permissions.can("updateJob") ? "none" : undefined}
          >
            {job?.paidAt ? "Mark as Unpaid" : "Mark as Paid"}
          </Button>
          <Button
            rightSection={<IconEdit />}
            onClick={() => navigate(`/app/jobs/${params.id}/update`)}
            display={!permissions.can("updateJob") ? "none" : undefined}
          >
            Update
          </Button>
          <DeleteButton
            id={jobId}
            onCompleted={() => navigate("/app/jobs")}
            type="job"
          />
        </Group>
      </Group>
      <Paper>
        <SimpleGrid cols={{ sm: 1, md: 2, lg: 3 }}>
          <Text>
            <strong>Customer Name:</strong>{" "}
            <Anchor
              onClick={(e) => {
                e.preventDefault();
                navigate(`/app/customers/${job?.customerId}`);
              }}
              href={`/app/customers/${job?.customerId}`}
            >
              {job?.customer?.firstName} {job?.customer?.lastName}
            </Anchor>
          </Text>
          <Text>
            <strong>Job Name:</strong> {job?.name}
          </Text>
          <Text>
            <strong>Racket:</strong> {job?.racket}
          </Text>
          <Text>
            <strong>Racket Type:</strong>{" "}
            {job && RACKET_TYPE_LABELS[job.racketType]}
          </Text>
          <Text>
            <strong>String:</strong> {job?.stringName}
          </Text>
          <Text>
            <strong>Gauge:</strong> {job?.gauge}
          </Text>
          <Text>
            <strong>Tension:</strong> {job?.tension}
          </Text>
          <Text>
            <strong>Status:</strong> {job?.status}
          </Text>
          <Text>
            <strong>Paid:</strong>{" "}
            {job?.paidAt ? (
              <>
                <Badge color="green">Paid</Badge>{" "}
                <DateDisplay value={job.paidAt} />
              </>
            ) : (
              <Badge color="red">Unpaid</Badge>
            )}
          </Text>
          <Text>
            <strong>Received At:</strong>{" "}
            <DateDisplay value={job?.receivedAt} />
          </Text>
          <Text>
            <strong>Finished At:</strong>{" "}
            <DateDisplay value={job?.finishedAt} />
          </Text>
          <Text>
            <strong>Finished By:</strong>{" "}
            <Anchor
              onClick={(e) => {
                e.preventDefault();
                navigate(`/app/employees/${job?.finishedById}`);
              }}
              href={`/app/employees/${job?.finishedById}`}
            >
              {job?.finishedBy?.firstName} {job?.finishedBy?.lastName}
            </Anchor>
          </Text>
          <Text>
            <strong>Updated At:</strong> <DateDisplay value={job?.updatedAt} />
          </Text>
          <Text>
            <strong>Created At:</strong> <DateDisplay value={job?.createdAt} />
          </Text>
        </SimpleGrid>
      </Paper>
    </>
  );
};

export default JobsDetailsPage;
